import { getLogger } from '@onefootprint/logger';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import type { WsMessage, WsOpenResult } from '@onefootprint/request-types/percy';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { ResourceContext } from '../types';
import { consumeInitialPrompt, peekInitialPrompt } from '../utils/initial-prompt';
import { getTaskStatus, isTerminalTaskStatus } from '../utils/journey-attribution';
import { notifyResourceArrival, notifyResourceOpen } from '../utils/resource-arrival-bus';
import { resourceKey } from '../utils/resource-presentation';
import { hasRunningJourneys, hasUnansweredQuestion } from '../utils/turn-notification-signals';
import { uploadChatFile } from './use-chat-files';

// task_progress.data is declared as `Any` on the Pydantic side, so the generated
// type is `unknown`. Keep the structured shape here until the Python side is tightened.
const { logWarn } = getLogger({ location: 'percy-chat-ws' });

export type TaskProgressData = {
  description: string;
  last_tool_name?: string;
  task_id: string;
  tool_use_id?: string;
  usage?: { total_tokens?: number; tool_uses?: number; duration_ms?: number };
};

/** This message type doesn't exist on the backend, only on our frontend. */
export type PendingMessage = {
  type: 'pending';
  content: string;
  file_names?: string[];
  question_id?: string;
  /** Resources the chat is about; mirrors the acknowledged user message's `context`
   * (a single object when sent optimistically, a list once acknowledged). */
  context?: ResourceContext | ResourceContext[] | null;
  /** Resources the user @-mentioned in this message; mirrors the ack's `mentions`. */
  mentions?: ResourceContext[];
  /**
   * Client-generated id sent with the message and echoed back by the backend, so the
   * matching acknowledgement removes this exact row even when several pendings share
   * the same text. Empty only on the pre-connect optimistic bubble and storybook
   * stubs, neither of which is ever matched against an ack.
   */
  id: string;
  /** Always undefined for pending rows */
  timestamp?: string;
};

/** This message type doesn't exist on the backend, only on our frontend. */
type SystemMessage = { type: 'system'; content: string; timestamp: string; is_thinking: boolean };

export type ChatMessage = FilteredServerMessage | SystemMessage;

/**
 * Normalized "a resource the chat touched changed, go refresh" notification. A
 * single channel (`onResourceEvent`) replaces the old per-resource callbacks
 * (`onArtifactSaved` / `onUiBlockGenerated` / `onTurnComplete`). `resource` is the
 * shared backend `ResourceContext` — consumers switch on `resource.kind`.
 */
export type ChatResourceEvent = {
  action: 'created' | 'updated';
  resource: ResourceContext;
};

export type ChatApprovalRequiredEvent = {
  approvalId: string;
  toolName?: string;
};

export type ChatTurnCompleteEvent = {
  isError: boolean;
  wasInterrupted: boolean;
  sessionId?: string;
  durationMs?: number;
  hasPendingQuestion: boolean;
  hasRunningJourneys: boolean;
};

// The subagent-attribution fields ship on every new frame (the generated type marks
// them required), but frames persisted before they existed replay without them — so
// locally they are optional, and reads go through `getParentToolUseId`/`getFrameAgent`.
type WithOptionalAttribution<T> = Omit<T, 'parent_tool_use_id' | 'agent'> & {
  parent_tool_use_id?: string;
  agent?: string;
};

// Generated `WsMessage` (the bare event/control-frame union) with local overrides
// for fields that Pydantic declares as `Any` (and so come through as `unknown`):
//  - `task_progress.data` narrowed to `TaskProgressData`.
//  - `tool_result.content` narrowed to `string`.
type FilteredServerMessage =
  | Exclude<WsMessage, { type: 'task_progress' | 'tool_result' | 'text' | 'thinking' | 'tool_use' }>
  | WithOptionalAttribution<Extract<WsMessage, { type: 'text' }>>
  | WithOptionalAttribution<Extract<WsMessage, { type: 'thinking' }>>
  | WithOptionalAttribution<Extract<WsMessage, { type: 'tool_use' }>>
  | {
      type: 'task_progress';
      data: TaskProgressData;
      status?: string;
      parent_tool_use_id?: string;
      agent?: string;
      timestamp: string;
      is_thinking?: boolean;
    }
  | {
      type: 'tool_result';
      tool_use_id: string;
      content: string;
      is_error?: boolean;
      parent_tool_use_id?: string;
      agent?: string;
      timestamp: string;
      is_thinking?: boolean;
    };

export type UserMessage = Extract<FilteredServerMessage, { type: 'user' }>;
export type ResourceMessage = Extract<FilteredServerMessage, { type: 'resource' }>;
export type ApprovalRequiredMessage = Extract<FilteredServerMessage, { type: 'approval_required' }>;

type ReadyMessage = { type: 'ready'; event_history: FilteredServerMessage[] };
type HistoryBatchMessage = { type: 'history_batch'; events: FilteredServerMessage[] };
type PongMessage = { type: 'pong' };

/** The message types that may be received directly from the backend */
type ServerMessage = ReadyMessage | HistoryBatchMessage | PongMessage | FilteredServerMessage;

type UseChatWsOptions = {
  session?: ChatSessionResponse;
  /** Chat session id — claims the stashed initial prompt on connect so it's sent as the first message. */
  chatSessionId?: string;
  isLive?: boolean;
  /** Pre-loaded messages used to seed the chat panel (e.g. from `/chat_sessions/{id}/messages`). */
  initialMessages?: ChatMessage[];
  onConnectionLost?: () => void;
  /**
   * Called on every `ready` handshake — initial connection and each reconnect.
   * The agent keeps running while no client is connected, so consumers should
   * refetch agent-touched state here to catch anything missed in the gap.
   */
  onReady?: () => void;
  /**
   * Called when the agent creates or updates a resource the consumer may be
   * displaying (artifact, gen-ui block, agent/investigation/entity). Fires on
   * live frames only — never during the `ready` history replay; pair with
   * `onReady` to catch frames missed while disconnected.
   */
  onResourceEvent?: (event: ChatResourceEvent) => void;
  onTurnComplete?: (event: ChatTurnCompleteEvent) => void;
  onApprovalRequired?: (event: ChatApprovalRequiredEvent) => void;
};

const buildWsUrl = ({ modalBaseUrl, sessionId, sessionToken }: ChatSessionResponse) => {
  const scheme = modalBaseUrl.startsWith('https') ? 'wss' : 'ws';
  const host = modalBaseUrl.replace(/^https?:\/\//, '');
  return `${scheme}://${host}/sessions/${sessionId}/ws?token=${sessionToken}`;
};

const MAX_RECONNECT_ATTEMPTS = 5;
// 4004 = sandboxes have idled out; the session is gone for good. 4008/4010 are
// per-session terminal states from the WS handler. Retrying any of these just
// burns ~30s of exponential backoff and stacks duplicate "Session not found"
// frames in the chat — handle them as session-expired and let the parent
// auto-recover via resumeSession.
//
// 1006: a handshake-time reject (e.g. a 4003 auth reject before `accept()`) is
// hidden from JS and surfaces as 1006
const NO_RECONNECT_CODES = new Set([1006, 1008, 1011, 4000, 4001, 4003, 4004, 4008, 4010]);

const useChatWs = ({
  session,
  chatSessionId,
  isLive,
  initialMessages,
  onConnectionLost,
  onReady,
  onResourceEvent,
  onTurnComplete,
  onApprovalRequired,
}: UseChatWsOptions) => {
  const _queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages ?? []);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef(false);
  const sessionTerminatedRef = useRef(false);
  // Latched when the user interrupts. While true, clamp `is_thinking` to false
  // on incoming events — the backend keeps streaming in-flight `tool_use` /
  // `text` frames with `is_thinking: true` until the next `turn_complete`,
  // which would otherwise flicker the indicator back on.
  const interruptedUntilTurnCompleteRef = useRef(false);
  // Messages submitted before the WS is open (resumption / reconnect) are
  // queued here and flushed on the next 'ready'. Optimistic pending entries
  // are added immediately so the user sees their message right away.
  const sendQueueRef = useRef<Array<{ content: string; args: SendMessageArgs; id: string }>>([]);
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  // The resource-event/ready handlers close over rapidly-changing consumer
  // state (e.g. the investigation's dirty edits / drafts). The `ws.onmessage`
  // closure is only rebound when session/chatSessionId change, so route
  // through refs to avoid stale reads of that state.
  const onResourceEventRef = useRef(onResourceEvent);
  useEffect(() => {
    onResourceEventRef.current = onResourceEvent;
  }, [onResourceEvent]);
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);
  const onTurnCompleteRef = useRef(onTurnComplete);
  useEffect(() => {
    onTurnCompleteRef.current = onTurnComplete;
  }, [onTurnComplete]);
  const onApprovalRequiredRef = useRef(onApprovalRequired);
  useEffect(() => {
    onApprovalRequiredRef.current = onApprovalRequired;
  }, [onApprovalRequired]);
  const messagesRef = useRef<ChatMessage[]>(initialMessages ?? []);
  const updateMessages = (update: (previous: ChatMessage[]) => ChatMessage[]) => {
    const next = update(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
  };
  // Accumulates `history_batch` frames during connect-time replay; applied on `ready`.
  const historyBufferRef = useRef<ChatMessage[]>([]);

  const processMessage = (message: FilteredServerMessage) => (previousMessages: ChatMessage[]) => {
    // For dedup paths (task_progress, tool_use) we preserve the ORIGINAL
    // timestamp from the first frame — otherwise the row's age would reset every
    // time the SDK streams another token of the same payload.
    if (message.type === 'task_progress') {
      const { task_id } = message.data;
      const lastIdx = previousMessages.findLastIndex(m => m.type === 'task_progress' && m.data.task_id === task_id);
      if (lastIdx >= 0) {
        // A heartbeat racing the completion signal must not flip a terminal
        // lifecycle row back to running — the journey card would spin forever.
        if (
          isTerminalTaskStatus(getTaskStatus(previousMessages[lastIdx])) &&
          !isTerminalTaskStatus(getTaskStatus(message))
        ) {
          return previousMessages;
        }
        const updated = [...previousMessages];
        updated[lastIdx] = { ...message, timestamp: previousMessages[lastIdx].timestamp };
        return updated;
      }
    }

    if ((message.type === 'thinking' || message.type === 'text') && message.started_at) {
      // Thinking and assistant text stream token-by-token, then the assembled
      // `AssistantMessage` frame re-sends each block once it's complete. Both the
      // streamed partials and that finalizing frame share a stable `started_at`, so
      // coalesce a frame into the existing row with the same (type, started_at) —
      // growing it in place and preserving the original timestamp so its age doesn't
      // reset. We scan back (not just the last row) because a tool_use can land
      // between the streamed block and the frame that finalizes it, and stop at the
      // current turn's user message so a frame never merges into an earlier turn.
      const { type, started_at } = message;
      for (let i = previousMessages.length - 1; i >= 0; i--) {
        const m = previousMessages[i];
        if (m.type === 'user') break;
        // The streamed row carries this block's started_at and was appended when the
        // block began, so any row emitted before then (and everything older) can't be
        // it — stop scanning.
        if (m.timestamp < started_at) break;
        if (m.type === type && (m.type === 'thinking' || m.type === 'text') && m.started_at === started_at) {
          const updated = [...previousMessages];
          updated[i] = { ...message, timestamp: m.timestamp };
          return updated;
        }
      }
    }

    if (message.type === 'tool_use') {
      // The Claude Agent SDK streams partial `AssistantMessage` frames as the
      // model emits a tool_use's JSON token-by-token; the backend relays each
      // one as a separate WsToolUseEvent with the same id and a progressively
      // more complete `input`. Without dedup the FE accumulates N copies and
      // either flickers (stale partial inputs) or sticks on the first.
      const lastIdx = previousMessages.findLastIndex(m => m.type === 'tool_use' && m.id === message.id);
      if (lastIdx >= 0) {
        const updated = [...previousMessages];
        updated[lastIdx] = { ...message, timestamp: previousMessages[lastIdx].timestamp };
        return updated;
      }
    }

    return [...previousMessages, message];
  };

  // Key the connect effect on the wire identity, not the session object: after
  // a create, `session` swaps reference from the POST /chat_sessions response to
  // the refetched active-session response for the same underlying session.
  // Rerunning the effect on that swap would tear down a healthy socket right
  // after the handshake — and silently drop an initial message still awaiting
  // its file uploads in `transmit`.
  const wsUrl = session ? buildWsUrl(session) : undefined;

  useEffect(() => {
    if (!wsUrl) return;

    // Reset close/reconnect state so a refreshed session (e.g. after a
    // /chat_sessions resume) doesn't inherit the previous teardown's flags.
    intentionalCloseRef.current = false;
    reconnectAttemptRef.current = 0;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptRef.current = 0;
        historyBufferRef.current = [];

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = event => {
        // Orphaned sockets (from an effect re-run that started a new WS before this one finished closing)
        // stay registered in the backend's connected_clients and keep receiving broadcasts.
        // Ignore them so we  don't double-append frames into messages.
        if (ws !== wsRef.current) return;
        const messageStr = event.data as string;
        let message: ServerMessage;
        try {
          message = JSON.parse(messageStr);
        } catch {
          console.error('Failed to parse message', messageStr);
          return;
        }
        if (message.type === 'pong') return;

        // History replay arrives as batches before the terminal `ready`; buffer them and
        // apply on `ready`, keeping the first-load/reconnect logic in one place.
        if (message.type === 'history_batch') {
          historyBufferRef.current.push(...((message.events ?? []) as ChatMessage[]));
          return;
        }

        // `ready` is replay/handshake and doesn't reflect live thinking — skip
        // the isThinking write to avoid flicker between WS-open and the first
        // server frame (or the queued initial prompt that follows on `ready`).
        if (message.type !== 'ready') {
          const nextIsThinking = interruptedUntilTurnCompleteRef.current ? false : (message.is_thinking ?? false);
          setIsThinking(nextIsThinking);
        }

        if (message.type === 'interrupt') {
          // Ack from the backend that the interrupt was received. Not user-visible chat content —
          // `sendInterrupt` already inserted the "Interrupt sent." system message.
          return;
        }

        if (message.type === 'ready') {
          // Server streams history as `history_batch` frames (buffered above);
          // Prefer the buffer, fall back to `event_history`
          const events = historyBufferRef.current.length ? historyBufferRef.current : (message.event_history ?? []);
          historyBufferRef.current = [];
          // On first connection, load history. On reconnect, keep existing messages to avoid
          // losing data the server may not replay (e.g. tool_results).
          updateMessages(prev => (prev.length === 0 ? (events as ChatMessage[]) : prev));
          onReadyRef.current?.();
          // Drain any messages submitted while the WS was opening (resumption /
          // reconnect). Their pending UI was already rendered on submit.
          void flushQueue();
          // For decision sessions the backend auto-sends the first message, so
          // don't send the initial prompt from the frontend. `agentKind` only
          // rides on the active-session response; a freshly created chat session
          // (ChatSessionResponse) is always a copilot, so treat a missing value
          // as non-decision. Read through the ref so a session refetch landing
          // after connect is still seen.
          const currentSession = sessionRef.current;
          const agentKind = currentSession && 'agentKind' in currentSession ? currentSession.agentKind : undefined;
          if (
            chatSessionId &&
            agentKind !== 'decision' &&
            (!events.length || peekInitialPrompt(chatSessionId)?.resume)
          ) {
            const initialPrompt = consumeInitialPrompt(chatSessionId);
            if (initialPrompt) {
              const { prompt, files, context, mentions } = initialPrompt;
              // This is the first message on this websocket, send the initial message.
              sendMessage(prompt, { files, context, mentions });
            }
          }
          return;
        }
        updateMessages(processMessage(message));

        // Custom handling for some message types
        if (message.type === 'user') {
          // Remove the pending message once the backend acknowledges receipt
          setPendingMessages(prev => {
            const idx = prev.findIndex(m => m.id === message.id);
            if (idx === -1) return prev;
            return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
          });
        } else if (message.type === 'turn_complete') {
          onTurnCompleteRef.current?.({
            isError: message.is_error ?? false,
            wasInterrupted: interruptedUntilTurnCompleteRef.current,
            sessionId: message.session_id ?? undefined,
            durationMs: message.duration_ms ?? undefined,
            hasPendingQuestion: hasUnansweredQuestion(messagesRef.current),
            hasRunningJourneys: hasRunningJourneys(messagesRef.current),
          });
          interruptedUntilTurnCompleteRef.current = false;
        } else if (message.type === 'approval_required') {
          onApprovalRequiredRef.current?.({
            approvalId: message.approval_id,
            toolName: message.tool_name ?? undefined,
          });
        } else if (message.type === 'resource' && message.kind === 'open') {
          // Percy asked to show this page, which changes nothing to refresh. Like an arrival, it only
          // moves the page for a live frame, so reopening the chat never navigates again. Percy waits
          // for the answer, which this socket sends back.
          const resourceId = resourceKey(message.resource);
          notifyResourceOpen(message.resource, { chatSessionId, isLive }, outcome => {
            const result: WsOpenResult = { type: 'open_result', resource_id: resourceId, outcome };
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(result));
          });
        } else if (message.type === 'resource') {
          onResourceEventRef.current?.({
            action: message.kind === 'update' ? 'updated' : 'created',
            resource: message.resource,
          });
          // Signal the widget's fly-to-explorer animation. This runs for live frames only (the
          // `ready` history replay returns above), so a chat's backlog never animates.
          if (message.kind === 'create' && message.resource) {
            notifyResourceArrival(resourceKey(message.resource), message.resource.kind, { chatSessionId, isLive });
          }
        }
      };

      ws.onclose = (event: CloseEvent) => {
        if (ws !== wsRef.current) return; // Ignore orphaned sockets

        setIsConnected(false);
        setIsThinking(false);
        interruptedUntilTurnCompleteRef.current = false;
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        const serverRejected = NO_RECONNECT_CODES.has(event.code);
        if (serverRejected) {
          logWarn('[percy-ws] Server rejected connection, not reconnecting', undefined, { code: event.code });
        }

        if (
          !intentionalCloseRef.current &&
          !sessionTerminatedRef.current &&
          !serverRejected &&
          reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          const attempt = reconnectAttemptRef.current;
          const delay = Math.min(1000 * 2 ** attempt, 16000);
          reconnectAttemptRef.current = attempt + 1;
          reconnectTimerRef.current = setTimeout(connect, delay);
        } else if (!intentionalCloseRef.current) {
          setError(serverRejected ? 'Session expired or unavailable' : 'Failed to reconnect after multiple attempts');
          onConnectionLost?.();
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
      };
    };

    connect();

    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      const ws = wsRef.current;
      if (ws) {
        // Detach before close() — the handshake is async and any frames the buffered
        // before final close would otherwise still fire these handlers and write to React state.
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
        wsRef.current = null;
      }
    };
  }, [wsUrl, chatSessionId]);

  // Upload files and put a user message on the wire.
  // Does NOT add to pendingMessages — the caller handles optimistic state.
  const transmit = async (content: string, args: SendMessageArgs, id: string) => {
    const { files, questionId, context, mentions } = args;
    const currentSession = sessionRef.current;
    let uploadedFiles: File[] = [];
    let uploadedNames: string[] = [];
    let failedFiles: File[] = [];
    if (files?.length && currentSession) {
      // The WS message references files by name in Modal's filesystem, so the
      // Modal upload MUST complete before we send the message.
      const results = await Promise.all(
        files.map(async f => ({ file: f, result: await uploadChatFile(currentSession, f) })),
      );
      const ok = results.flatMap(r => (r.result.ok ? [{ file: r.file, path: r.result.path }] : []));
      uploadedFiles = ok.map(r => r.file);
      // Percy reads files back by the name they landed under, which the upload may
      // have rewritten to ASCII (Modal rejects non-ASCII URL paths).
      uploadedNames = ok.map(r => r.path);
      failedFiles = results.filter(r => !r.result.ok).map(r => r.file);
      if (failedFiles.length) {
        // Tell the user — and Percy, indirectly, by omitting failed names from
        // `file_names` below — that these never landed in Modal. Without this
        // the attach silently dropped and Percy would ask the user to re-attach
        // files that (from the UI's POV) had already gone through.
        const names = failedFiles.map(f => f.name).join(', ');
        const noun = failedFiles.length === 1 ? 'file' : 'files';
        updateMessages(prev => [
          ...prev,
          {
            type: 'system',
            content: `Couldn't upload ${noun}: ${names}. Please try attaching again.`,
            timestamp: new Date().toISOString(),
            is_thinking: false,
          },
        ]);
      }
    }
    // Read the socket only now: the upload await above can outlive the socket
    // that was open when the send started (reconnects, sandbox blips). Sending
    // on a closed socket silently discards the frame, so queue for the next
    // 'ready' instead — flushQueue re-transmits (re-uploads are idempotent PUTs).
    // Re-queue with only the successfully uploaded files so a retry doesn't
    // re-attempt — and possibly succeed for — files we've already told the user failed.
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) {
      sendQueueRef.current.push({ content, args: { ...args, files: uploadedFiles }, id });
      return;
    }
    ws.send(
      JSON.stringify({
        type: 'user',
        content,
        file_names: uploadedNames.length ? uploadedNames : undefined,
        question_id: questionId,
        context,
        mentions,
        id,
      }),
    );
  };

  const sendMessage = async (content: string, args: SendMessageArgs) => {
    const { files, questionId, context, mentions } = args;
    // One id per message, carried to the wire (and through the offline queue) so the
    // backend can echo it back and the ack handler removes this exact pending row.
    const id = crypto.randomUUID();
    // Optimistic UI: show the message and a thinking indicator immediately,
    // even if the WS isn't open yet (resumption / reconnect).
    setPendingMessages(prev => [
      ...prev,
      {
        type: 'pending',
        content,
        file_names: files?.map(f => f.name),
        question_id: questionId,
        context,
        mentions,
        id,
      },
    ]);
    // A new message starts a new turn — stop clamping `is_thinking` even if the
    // interrupted turn never produced a `turn_complete` (e.g. interrupt while idle).
    interruptedUntilTurnCompleteRef.current = false;
    setIsThinking(true);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      await transmit(content, args, id);
    } else {
      // Queue for the next 'ready' — flushQueue will transmit it.
      sendQueueRef.current.push({ content, args, id });
    }
  };

  const flushQueue = async () => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    const queue = sendQueueRef.current;
    sendQueueRef.current = [];
    for (const { content, args, id } of queue) {
      await transmit(content, args, id);
    }
  };

  // `silent` is for callers that show their own event in the conversation, like ending a walkthrough.
  const sendInterrupt = ({ silent = false }: { silent?: boolean } = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'interrupt' }));
      if (!silent) {
        updateMessages(prev => [
          ...prev,
          { type: 'system', content: 'Interrupt sent.', timestamp: new Date().toISOString(), is_thinking: false },
        ]);
      }
      interruptedUntilTurnCompleteRef.current = true;
      setIsThinking(false);
    }
  };

  // approveAll asks Percy to skip the card for later calls to the same tool this session.
  const sendApproval = (approvalId: string, approved: boolean, approveAll = false) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(
      JSON.stringify({ type: 'tool_approval', approval_id: approvalId, approved, approve_all: approveAll }),
    );
  };

  const sendCancelMessage = (id: string) => {
    // Optimistic: drop the bubble now. Also strip from the offline send queue in case
    // it was buffered (WS not yet open) and never actually transmitted to the backend.
    setPendingMessages(prev => prev.filter(m => m.id !== id));
    sendQueueRef.current = sendQueueRef.current.filter(q => q.id !== id);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel_message', id }));
    }
  };

  return {
    messages,
    /**
     * Messages are considered pending until the backend acknowledges receipt of them. If
     * another query, it may not acknowledge receipt for some time. Keep a separate log of them
     * and render them separately until they are acknowledged.
     */
    pendingMessages,
    sendMessage,
    sendInterrupt,
    sendApproval,
    sendCancelMessage,
    isConnected,
    isThinking,
    error,
  };
};

export type SendMessageArgs = {
  files?: File[];
  questionId?: string;
  /** Resource the chat is about; the backend names it in each user turn. */
  context?: ResourceContext;
  /** Resources the user @-mentioned in this message. Snapshotted at send time so
   * historical messages keep the name they had when sent. */
  mentions?: ResourceContext[];
};

export default useChatWs;
